/**
 * textParse.js — Parse a free-text bet description into a DRAFT bet
 * record (see engine/model.js). Framework-free ES2020, same portability
 * rules as the engine: the same file runs in Node tests, the website,
 * and the app.
 *
 * The parser is deliberately conservative: it NEVER guesses silently.
 * Anything it could not find is reported in `missing` so the confirm
 * form can demand it from the user; anything it inferred with less than
 * full confidence is reported in `warnings`.
 *
 *   parseBetText(text) -> { draft, missing: [...], warnings: [...] }
 *
 * `draft` is a model bet record with status 'draft' and source 'text'.
 * A draft is NOT a bet yet: stake may be 0 and oddsAmerican may be null
 * while they are listed in `missing`. The UI's confirm step must fill
 * every `missing` field before the draft can become a pending bet.
 *
 * Supported shapes (see tests):
 *   "Chiefs -3.5 $50 @ -110 DK"
 *   "Lakers ML 25 +130 FanDuel"            (bare-number stake, warned)
 *   "Over 224.5 in the Knicks game $20 -110"
 *   "Yankees over 8.5, $15"
 *   "parlay: Chiefs -3.5 + Lakers ML $50 @ +260 DK"
 *   "Chiefs ML + Over 47.5 $30 +320"       (legs split on " + ")
 *
 * TEAM MATCHING uses the small alias table below, which covers the
 * teams in the prototype's sample data plus the examples above. In
 * production this table is replaced by the site's full team directory
 * (every league, every team, maintained in one place) — the parser
 * only depends on the { name, short, sport, aliases } shape.
 */

import { createBet } from '../engine/model.js';

/**
 * Team alias table. `name` is the full display name (used to build the
 * event string), `short` is the short name used in selection strings
 * (matching the hub's existing conventions, e.g. "Chiefs -3.5",
 * "Lakers ML"), `aliases` are extra match phrases.
 */
export const TEAM_ALIASES = Object.freeze([
  // NFL
  { name: 'Kansas City Chiefs', short: 'Chiefs', sport: 'NFL', aliases: ['kansas city'] },
  { name: 'Buffalo Bills', short: 'Bills', sport: 'NFL', aliases: [] },
  { name: 'Miami Dolphins', short: 'Dolphins', sport: 'NFL', aliases: [] },
  { name: 'New York Jets', short: 'Jets', sport: 'NFL', aliases: [] },
  { name: 'Baltimore Ravens', short: 'Ravens', sport: 'NFL', aliases: [] },
  { name: 'Pittsburgh Steelers', short: 'Steelers', sport: 'NFL', aliases: [] },
  { name: 'Green Bay Packers', short: 'Packers', sport: 'NFL', aliases: [] },
  { name: 'Chicago Bears', short: 'Bears', sport: 'NFL', aliases: [] },
  { name: 'Denver Broncos', short: 'Broncos', sport: 'NFL', aliases: [] },
  { name: 'New England Patriots', short: 'Patriots', sport: 'NFL', aliases: [] },
  { name: 'Philadelphia Eagles', short: 'Eagles', sport: 'NFL', aliases: [] },
  { name: 'Dallas Cowboys', short: 'Cowboys', sport: 'NFL', aliases: [] },
  { name: 'San Francisco 49ers', short: '49ers', sport: 'NFL', aliases: ['niners'] },
  { name: 'Seattle Seahawks', short: 'Seahawks', sport: 'NFL', aliases: [] },
  { name: 'Detroit Lions', short: 'Lions', sport: 'NFL', aliases: [] },
  { name: 'Las Vegas Raiders', short: 'Raiders', sport: 'NFL', aliases: [] },
  { name: 'Cincinnati Bengals', short: 'Bengals', sport: 'NFL', aliases: [] },
  // NBA
  { name: 'Los Angeles Lakers', short: 'Lakers', sport: 'NBA', aliases: [] },
  { name: 'New York Knicks', short: 'Knicks', sport: 'NBA', aliases: [] },
  { name: 'Boston Celtics', short: 'Celtics', sport: 'NBA', aliases: [] },
  { name: 'Golden State Warriors', short: 'Warriors', sport: 'NBA', aliases: [] },
  { name: 'Phoenix Suns', short: 'Suns', sport: 'NBA', aliases: [] },
  { name: 'Denver Nuggets', short: 'Nuggets', sport: 'NBA', aliases: [] },
  { name: 'Miami Heat', short: 'Heat', sport: 'NBA', aliases: [] },
  { name: 'Los Angeles Clippers', short: 'Clippers', sport: 'NBA', aliases: [] },
  { name: 'Dallas Mavericks', short: 'Mavericks', sport: 'NBA', aliases: ['mavs'] },
  { name: 'Minnesota Timberwolves', short: 'Timberwolves', sport: 'NBA', aliases: ['wolves'] },
  { name: 'Milwaukee Bucks', short: 'Bucks', sport: 'NBA', aliases: [] },
  { name: 'Toronto Raptors', short: 'Raptors', sport: 'NBA', aliases: [] },
  { name: 'Detroit Pistons', short: 'Pistons', sport: 'NBA', aliases: [] },
  { name: 'San Antonio Spurs', short: 'Spurs', sport: 'NBA', aliases: [] },
  { name: 'Sacramento Kings', short: 'Kings', sport: 'NBA', aliases: [] },
  { name: 'Brooklyn Nets', short: 'Nets', sport: 'NBA', aliases: [] },
  { name: 'Portland Trail Blazers', short: 'Trail Blazers', sport: 'NBA', aliases: ['blazers'] },
  { name: 'Oklahoma City Thunder', short: 'Thunder', sport: 'NBA', aliases: [] },
  { name: 'Philadelphia 76ers', short: '76ers', sport: 'NBA', aliases: ['sixers'] },
  // MLB
  { name: 'New York Yankees', short: 'Yankees', sport: 'MLB', aliases: [] },
  { name: 'Boston Red Sox', short: 'Red Sox', sport: 'MLB', aliases: [] },
  { name: 'Houston Astros', short: 'Astros', sport: 'MLB', aliases: [] },
  { name: 'Seattle Mariners', short: 'Mariners', sport: 'MLB', aliases: [] },
  { name: 'Los Angeles Dodgers', short: 'Dodgers', sport: 'MLB', aliases: [] },
  { name: 'San Diego Padres', short: 'Padres', sport: 'MLB', aliases: [] },
  { name: 'Milwaukee Brewers', short: 'Brewers', sport: 'MLB', aliases: [] },
  { name: 'Tampa Bay Rays', short: 'Rays', sport: 'MLB', aliases: [] },
  { name: 'Chicago Cubs', short: 'Cubs', sport: 'MLB', aliases: [] },
  { name: 'St. Louis Cardinals', short: 'Cardinals', sport: 'MLB', aliases: [] },
]);

/** Book names/keys recognized in free text (keys match linkParse.js). */
const TP_BOOKS = Object.freeze([
  { key: 'draftkings', name: 'DraftKings', phrases: ['draftkings', 'draft kings', 'dk'] },
  { key: 'fanduel', name: 'FanDuel', phrases: ['fanduel', 'fan duel', 'fd'] },
  { key: 'betmgm', name: 'BetMGM', phrases: ['betmgm', 'bet mgm', 'mgm'] },
  { key: 'caesars', name: 'Caesars', phrases: ['caesars'] },
  { key: 'betrivers', name: 'BetRivers', phrases: ['betrivers', 'bet rivers'] },
  { key: 'espnbet', name: 'ESPN BET', phrases: ['espnbet', 'espn bet'] },
  { key: 'fanatics', name: 'Fanatics', phrases: ['fanatics'] },
  { key: 'bet365', name: 'bet365', phrases: ['bet365'] },
  { key: 'hardrock', name: 'Hard Rock Bet', phrases: ['hard rock bet', 'hardrock', 'hard rock'] },
]);

function tpEscapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** All phrases that can refer to a team, longest first for matching. */
function tpTeamPhrases() {
  const phrases = [];
  for (const team of TEAM_ALIASES) {
    phrases.push({ phrase: team.name.toLowerCase(), team });
    phrases.push({ phrase: team.short.toLowerCase(), team });
    for (const alias of team.aliases) phrases.push({ phrase: alias, team });
  }
  phrases.sort((a, b) => b.phrase.length - a.phrase.length);
  return phrases;
}
const TP_PHRASES = tpTeamPhrases();

/**
 * Find team mentions in lowercased text. Returns non-overlapping
 * matches sorted by position: [{ team, start, end }].
 */
function tpFindTeams(lower) {
  const found = [];
  const claimed = [];
  for (const { phrase, team } of TP_PHRASES) {
    const re = new RegExp('\\b' + tpEscapeRe(phrase) + '\\b', 'g');
    let m;
    while ((m = re.exec(lower)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      if (claimed.some(([s, e]) => start < e && end > s)) continue;
      claimed.push([start, end]);
      found.push({ team, start, end });
    }
  }
  found.sort((a, b) => a.start - b.start);
  // Dedupe repeated mentions of the same team (keep the first).
  const seen = new Set();
  return found.filter((f) => {
    if (seen.has(f.team.name)) return false;
    seen.add(f.team.name);
    return true;
  });
}

/**
 * Parse one leg / single bet from a lowercased, already-stripped
 * segment (book, stake-with-$, and odds already removed globally).
 * Returns { team, sport, betType, line, selection, event, rest,
 * warnings } where `rest` is the unconsumed text (used by the caller
 * to spot a bare-number stake).
 */
function tpParseLeg(segment) {
  const warnings = [];
  let rest = segment;
  const teams = tpFindTeams(segment);
  const team = teams.length ? teams[0].team : null;
  const opponent = teams.length > 1 ? teams[1].team : null;
  if (!team) warnings.push('no team matched — check the selection before confirming');

  let betType = null;
  let line = null;
  let selection = '';

  const totalMatch = rest.match(/\b(over|under)\s*(\d+(?:\.\d+)?)/);
  const mlMatch = rest.match(/\b(ml|moneyline)\b/);
  if (totalMatch) {
    betType = 'total';
    line = parseFloat(totalMatch[2]);
    selection = (totalMatch[1] === 'over' ? 'Over ' : 'Under ') + line;
    rest = rest.replace(totalMatch[0], ' ');
  } else if (mlMatch) {
    betType = 'moneyline';
    selection = team ? team.short + ' ML' : '';
    rest = rest.replace(mlMatch[0], ' ');
  } else if (team) {
    // Spread: a signed number right after the (first) team mention.
    const first = teams[0];
    const after = segment.slice(first.end);
    const lineMatch = after.match(/^\s*\(?\s*([+-]\d+(?:\.\d+)?)\s*\)?/);
    if (lineMatch) {
      betType = 'spread';
      line = parseFloat(lineMatch[1]);
      selection = team.short + ' ' + (line > 0 ? '+' + line : '' + line);
      // Consume the line so the caller's bare-stake scan cannot
      // mistake "-3.5" for an amount of money.
      rest = rest.replace(lineMatch[1], ' ');
    } else {
      betType = 'moneyline';
      selection = team.short + ' ML';
      warnings.push('no line or "ML" marker found — assumed moneyline');
    }
  }

  // Event string: "A at B" when two teams were named, else "<Team> game".
  let event = '';
  if (team && opponent) event = team.name + ' at ' + opponent.name;
  else if (team) event = team.name + ' game';

  // Whatever is left after removing team mentions and filler words is
  // returned for the bare-stake scan.
  if (team) {
    for (const t of teams) {
      rest = rest.replace(segment.slice(t.start, t.end), ' ');
    }
  }
  rest = rest.replace(/\b(in|the|game|at|vs|@|a|an|on|for|to|win)\b/g, ' ');
  rest = rest.replace(/\s+/g, ' ').trim();

  return {
    team, sport: team ? team.sport : '', betType, line, selection, event,
    rest, warnings,
  };
}

/**
 * Parse free text into a draft bet. See the file header for the
 * contract. Throws nothing.
 */
export function parseBetText(rawText) {
  const warnings = [];
  const missing = [];
  const original = String(rawText ?? '');
  let work = ' ' + original.toLowerCase().replace(/[−–—]/g, '-').replace(/,/g, ' ') + ' ';

  const parlayHint = /\bparlay\b/.test(work);
  work = work.replace(/\bparlay\s*:/, ' ');

  // --- book (global) ---
  let book = null;
  for (const b of TP_BOOKS) {
    let matched = false;
    for (const phrase of b.phrases) {
      const re = new RegExp('\\b' + tpEscapeRe(phrase) + '\\b');
      if (re.test(work)) {
        work = work.replace(re, ' ');
        book = { key: b.key, name: b.name };
        matched = true;
        break;
      }
    }
    if (matched) break;
  }

  // --- stake with an explicit $ (global) ---
  let stake = null;
  const stakeMatch = work.match(/\$\s*(\d+(?:\.\d{1,2})?)/);
  if (stakeMatch) {
    stake = parseFloat(stakeMatch[1]);
    work = work.replace(stakeMatch[0], ' ');
  }

  // --- odds (global): "@ -110", or a bare signed integer >= 100 ---
  let odds = null;
  const atOdds = work.match(/@\s*([+-]?\d{2,4})\b/);
  if (atOdds) {
    odds = parseInt(atOdds[1], 10);
    work = work.replace(atOdds[0], ' ');
  } else {
    const bare = work.match(/\s([+-]\d{3,4})\b/);
    if (bare) {
      odds = parseInt(bare[1], 10);
      work = work.replace(bare[0], ' ');
    }
  }
  if (odds !== null && Math.abs(odds) < 100) {
    warnings.push('odds "' + odds + '" look wrong for American odds — double-check');
  }

  // --- split into legs ---
  const segments = work.split(/\s\+\s/).map((s) => s.trim()).filter(Boolean);
  const isParlay = parlayHint || segments.length > 1;
  if (parlayHint && segments.length < 2) {
    warnings.push('"parlay" mentioned but no legs separated by " + " were found — treated as a single bet');
  }

  let draft;
  if (isParlay && segments.length > 1) {
    const legs = [];
    const legSports = new Set();
    for (const seg of segments) {
      const leg = tpParseLeg(seg);
      warnings.push(...leg.warnings);
      if (leg.sport) legSports.add(leg.sport);
      legs.push({
        event: leg.event,
        selection: leg.selection,
        betType: leg.betType ?? 'moneyline',
        ...(leg.line !== null ? { line: leg.line } : {}),
      });
      // A leg's rest may hold the parlay's bare-number stake.
      if (stake === null && leg.rest) {
        const bareNum = leg.rest.match(/(\d+(?:\.\d{1,2})?)/);
        if (bareNum) {
          stake = parseFloat(bareNum[1]);
          warnings.push('stake read from a bare number (no $) — confirm the amount');
        }
      }
    }
    const sport = legSports.size === 1 ? [...legSports][0] : (legs.length ? (tpFindTeams(segments[0])[0]?.team.sport ?? '') : '');
    if (legSports.size > 1) warnings.push('parlay legs span multiple sports — sport set to the first leg');
    draft = createBet({
      book: book ?? undefined,
      sport,
      league: sport,
      event: legs.length + '-leg parlay',
      selection: legs.map((l) => l.selection).join(' + '),
      betType: 'parlay',
      legs,
      oddsAmerican: odds ?? undefined,
      stake: stake ?? 0,
      status: 'draft',
      source: 'text',
    });
  } else {
    const leg = tpParseLeg(work.trim());
    warnings.push(...leg.warnings);
    if (stake === null && leg.rest) {
      const bareNum = leg.rest.match(/(\d+(?:\.\d{1,2})?)/);
      if (bareNum) {
        stake = parseFloat(bareNum[1]);
        warnings.push('stake read from a bare number (no $) — confirm the amount');
      }
    }
    draft = createBet({
      book: book ?? undefined,
      sport: leg.sport,
      league: leg.sport,
      event: leg.event,
      selection: leg.selection,
      betType: leg.betType ?? 'moneyline',
      line: leg.line,
      oddsAmerican: odds ?? undefined,
      stake: stake ?? 0,
      status: 'draft',
      source: 'text',
    });
    if (!leg.selection) missing.push('selection');
  }

  if (odds === null) {
    // Do NOT let the model default (-110) masquerade as parsed data.
    draft.oddsAmerican = null;
    missing.push('odds');
  }
  if (stake === null) missing.push('stake');
  if (book === null) missing.push('book');

  return { draft, missing, warnings };
}
